import { type Component } from 'solid-js';
import { hydrate, render } from 'solid-js/web';
import type { IIslandsSet, IStaticHandlersSet } from '../../types/index.js';

/**
 * Hydrate islands statically, using the input handlers.
 * @param islands Islands set read from the server serialized context
 * @param handlers Key-value pairs to match each component name with its function handler.
 */
export const hydrateStatic = (islands: IIslandsSet, handlers: IStaticHandlersSet): void => {
  Object.entries(islands).forEach(([id, islandHandler]) => {
    const { componentName, componentProps, csrOnly } = islandHandler;
    // Get the proper component fn handler, by component name.
    const Comp = handlers[componentName];
    if (!Comp) {
      // eslint-disable-next-line no-console
      console.error('Island component could not be loaded, make sure pass a handler with a matching name!', { componentName, handlers });
      return;
    }

    // Get the island component host.
    const componentHost = document.getElementById(id);
    if (componentHost) {
      if (!csrOnly) {
        // https://www.solidjs.com/docs/latest/api#hydrate
        hydrate(() => <Comp {...componentProps} />, componentHost);
        return;
      }
      // https://www.solidjs.com/docs/latest/api#render
      render(() => <Comp {...componentProps} />, componentHost);
    }
  });
};

export const hydrateIslands = (
  islands: IIslandsSet,
  islandLoaders: Record<string, () => Promise<Component<Record<string, unknown>>>>,
): void => {
  document.querySelectorAll('[data-island]').forEach(async (node: Element) => {
    const islandComponent = node.getAttribute('data-island');
    const id = node.id;
    const { componentProps, csrOnly } = islands[id];
    if (!islandComponent || !id) {
      // eslint-disable-next-line no-console
      console.error('Some island data is not there for hydration', { islandComponent, id, islandData: islands[id] });
      return;
    }
    const componentLoader = islandLoaders[islandComponent];
    if (!componentLoader) {
      // eslint-disable-next-line no-console
      console.error('Island componentLoader is not available, make sure the module tsup config includes this brand!', {
        islandComponent,
        islandLoaders,
      });
      return;
    }
    const Comp = await componentLoader();
    if (!Comp) {
      // eslint-disable-next-line no-console
      console.error('Island component could not be loaded:', { islandComponent, componentLoader });
      return;
    }

    if (!csrOnly) {
      // https://www.solidjs.com/docs/latest/api#hydrate
      hydrate(() => <Comp {...componentProps} />, node);
      return;
    }
    // https://www.solidjs.com/docs/latest/api#render
    render(() => <Comp {...componentProps} />, node);
  });
};
